import { router } from '@inertiajs/react';
import { Trash2 } from 'lucide-react';
import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';

interface DeleteSponsorCreditDialogProps {
  creditId: string;
  sponsorSlug: string;
  eventSlug: string;
  hackerName: string;
}

export function DeleteSponsorCreditDialog({
  creditId,
  sponsorSlug,
  eventSlug,
  hackerName,
}: DeleteSponsorCreditDialogProps) {
  const [open, setOpen] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = () => {
    setError(null);
    setIsPending(true);
    router.delete(
      `/admin/${eventSlug}/sponsors/${sponsorSlug}/credits/${creditId}`,
      {
        preserveScroll: true,
        onSuccess: () => setOpen(false),
        onError: (errors) =>
          setError(
            (errors as unknown as Record<string, string | undefined>).delete ??
              'Failed to delete',
          ),
        onFinish: () => setIsPending(false),
      },
    );
  };

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setOpen(true)}
        className="text-red-600 hover:bg-red-50 hover:text-red-700"
      >
        <Trash2 className="h-4 w-4" />
      </Button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Credit</AlertDialogTitle>
            <AlertDialogDescription>
              Remove credit for <strong>{hackerName}</strong>? This cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 p-3">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isPending}
              className="bg-red-600 hover:bg-red-700"
            >
              {isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
